import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { pad } from '@/lib/format'
import { usePrefersReducedMotion } from '@/hooks/use-media-query'
import { ConjugationStatic } from '@/components/landing/conjugation'
import { statement } from '@/constants/content'

type StatementEntry = (typeof statement.words)[number]

interface RevealWordProps {
    entry: StatementEntry
    index: number
    total: number
    progress: MotionValue<number>
    reduced: boolean
}

const RevealWord: FC<RevealWordProps> = ({ entry, index, total, progress, reduced }): ReactNode => {
    const start = index / total
    const opacity = useTransform(progress, [start, start + 1 / total], [0.22, 1])

    if (typeof entry === 'string') {
        return (
            <>
                <motion.span style={{ opacity: reduced ? 1 : opacity }} className='inline-block'>
                    {entry}
                </motion.span>{' '}
            </>
        )
    }

    return (
        <>
            <motion.span style={{ opacity: reduced ? 1 : opacity }} className='inline-block'>
                <span className='font-serif font-normal italic tracking-normal text-tone-accent'>
                    {entry.word}
                </span>

                <sup className='ml-1 font-heading text-[0.3em] font-semibold tabular-nums text-tone-accent'>
                    {pad(entry.gift)}
                </sup>

                {entry.trailing}
            </motion.span>{' '}
        </>
    )
}

// The manifesto statement. Its opening lines (Unë jap, Ti jep, Ne japim) play inside the hero stage.
export const Manifesto: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduced = usePrefersReducedMotion()
    const total = statement.words.length

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start 0.85', 'end 0.55']
    })

    return (
        <section
            id='manifesti-deklarata'
            data-tone='blue'
            aria-labelledby='manifesti-titulli'
            className='relative'
        >
            <h2 id='manifesti-titulli' className='sr-only'>
                Manifesti i JAP-it
            </h2>

            {reduced && <ConjugationStatic />}

            <div className='grid grid-cols-12 gap-x-5 px-5 pb-28 pt-16 md:px-8 md:pb-36 md:pt-24 lg:px-12'>
                <div className='col-span-12 mb-10 md:col-span-3 md:mb-0'>
                    <p className='flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted md:sticky md:top-28'>
                        <span className='font-heading tabular-nums text-tone-accent'>02</span>
                        <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                        {statement.label}
                    </p>
                </div>

                <div ref={ref} className='col-span-12 md:col-span-9'>
                    <p className='sr-only'>{statement.full}</p>

                    <p
                        aria-hidden='true'
                        className='font-heading text-[2.1rem] font-medium leading-[1.1] tracking-[-0.03em] sm:text-5xl lg:text-[4vw]'
                    >
                        {statement.words.map((entry, index) => (
                            <RevealWord
                                key={`${typeof entry === 'string' ? entry : entry.word}-${index}`}
                                entry={entry}
                                index={index}
                                total={total}
                                progress={scrollYProgress}
                                reduced={reduced}
                            />
                        ))}
                    </p>

                    <div className='mt-14 grid gap-8 md:mt-20 md:grid-cols-9 md:gap-x-5'>
                        <p className='text-lg leading-relaxed text-tone-fg/85 md:col-span-5 md:col-start-5 md:text-xl'>
                            {statement.aim}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
